import api from './api';
import { handleResponse, handleError } from './api';

/**
 * General Portal "send a signal" — the public counterpart to Portal's
 * signals. Any user can broadcast to friends or the public (Local/International
 * radius). Backed by /api/signals (SignalScope == "public"); strictly separate
 * from Portal's /api/portal/signals.
 */
const signalService = {
  // The People-feed signal list. Pass the viewer's coords so "local" public
  // signals can be distance-filtered.
  async getFeed({ lat = null, lng = null } = {}) {
    const params = {};
    if (lat != null) params.lat = lat;
    if (lng != null) params.lng = lng;
    try { return handleResponse(await api.get('/signals/feed', { params })); }
    catch (e) { return handleError(e); }
  },

  // All publicly-open signals near a location — the Events-tab "Signals" list.
  /** The viewer's live Portals opened from an event: [{ sourceEventId, portalId, inviteCode }].
   *  Event cards use it to show PORTAL OPEN instead of offering a second one. */
  async mySources() {
    try { return handleResponse(await api.get('/signals/mine/sources')); }
    catch (e) { return handleError(e); }
  },

  async getPublicOpen({ lat = null, lng = null } = {}) {
    const params = {};
    if (lat != null) params.lat = lat;
    if (lng != null) params.lng = lng;
    try { return handleResponse(await api.get('/signals/public', { params })); }
    catch (e) { return handleError(e); }
  },

  /** Active club-scoped signals for a club's members (Phase 4). */
  async getClubSignals(clubId) {
    try { return handleResponse(await api.get(`/signals/club/${clubId}`)); }
    catch (e) { return handleError(e); }
  },

  // req: { signalText, audience, radius, mode, startUtc, endUtc, latitude, longitude, locationLabel, clubId }
  async create(req) {
    try { return handleResponse(await api.post('/signals', req)); }
    catch (e) { return handleError(e); }
  },

  async get(eventId) {
    try { return handleResponse(await api.get(`/signals/${eventId}`)); }
    catch (e) { return handleError(e); }
  },

  // Same payload shape as create; edits an existing regular signal.
  async update(eventId, req) {
    try { return handleResponse(await api.put(`/signals/${eventId}`, req)); }
    catch (e) { return handleError(e); }
  },

  /** Everyone a signal went to — the full list behind the "+N" tile. */
  async getInvitees(eventId) {
    try {
      const response = await api.get(`/signals/${eventId}/invitees`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Hide a signal from your own wall. Not a report — the sender is never told and
   * nobody else's wall changes. DELETE the same path to undo.
   */
  async dismiss(eventId) {
    try {
      const response = await api.post(`/signals/${eventId}/dismiss`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async undismiss(eventId) {
    try {
      const response = await api.delete(`/signals/${eventId}/dismiss`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** The Portal behind a short link: { eventId }. */
  async byCode(code) {
    try { return handleResponse(await api.get(`/signals/by-code/${encodeURIComponent(code)}`)); }
    catch (e) { return handleError(e); }
  },

  async imDown(eventId) {
    try { return handleResponse(await api.post(`/signals/${eventId}/im-down`)); }
    catch (e) { return handleError(e); }
  },

  async withdraw(eventId) {
    try { return handleResponse(await api.post(`/signals/${eventId}/withdraw`)); }
    catch (e) { return handleError(e); }
  },

  // Host-only: applicants for a curated signal — welcome in / not this one.
  async listApplicants(eventId) {
    try { return handleResponse(await api.get(`/signals/${eventId}/applicants`)); }
    catch (e) { return handleError(e); }
  },

  // The guest list — who's coming. Server returns it only to the host or someone
  // who's in, and an empty list to anyone else.
  async listAttendees(eventId) {
    try { return handleResponse(await api.get(`/signals/${eventId}/attendees`)); }
    catch (e) { return handleError(e); }
  },

  // action: 'accept' | 'decline'. On decline, `note` is an optional host line
  // shown privately to the applicant on their "NOT THIS ONE" outcome.
  async reviewApplicant(eventId, applicantUserId, action, note) {
    try { return handleResponse(await api.post(`/signals/${eventId}/applicants/${applicantUserId}/review`, { action, note: note || null })); }
    catch (e) { return handleError(e); }
  },

  async remove(eventId) {
    try { return handleResponse(await api.delete(`/signals/${eventId}`)); }
    catch (e) { return handleError(e); }
  },
};

export default signalService;
